import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import { useNavigate } from 'react-router-dom'
import { timeAgo } from '../../hooks/useTimeAgo'

// Coding Lab group chat
const LAB_CHAT_ID = 'conv-20260403123702-77bb39'

// ─── Types ───

interface Category {
  id: string
  title: string
  description: string | null
  icon: string | null
  scope: string | null
  sort_order: number
}

interface Resource {
  id: string
  title: string
  url: string | null
  category: string
  description: string | null
  summary: string | null
  tags: string[]
  added_by: string | null
  created_at: string
}

interface Idea {
  id: string
  title: string
  description: string | null
  category: string
  category_id: string | null
  status: string
  priority: string
  complexity: string
  assigned_to: string[]
  proposed_by: string | null
  conversation_id: string | null
  project_id: string | null
  tags: string[]
  votes: number
  created_at: string
  updated_at: string
}

interface LabStats {
  total_categories?: number
  total_resources: number
  total_ideas: number
  total_projects: number
  ideas_by_status: Record<string, number>
}

// ─── Constants ───

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  proposed: { bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' },
  approved: { bg: 'rgba(96,165,250,0.15)', text: '#60a5fa' },
  'in-progress': { bg: 'rgba(52,211,153,0.15)', text: '#34d399' },
  testing: { bg: 'rgba(251,191,36,0.15)', text: '#fbbf24' },
  shipped: { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
  archived: { bg: 'rgba(107,114,128,0.15)', text: '#6b7280' },
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#6b7280',
}

const CATEGORY_ICONS: Record<string, string> = {
  repo: '📦',
  tool: '🔧',
  article: '📄',
  video: '🎬',
  paper: '📑',
  framework: '🏗️',
  other: '🔗',
}

const IDEA_CATEGORY_ICONS: Record<string, string> = {
  feature: '✨',
  optimization: '⚡',
  infrastructure: '🏗️',
  ux: '🎨',
  security: '🔒',
  research: '🔬',
  integration: '🔌',
}

// ─── Components ───

function StatusBadge({ status }: { status: string }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.proposed
  return (
    <span
      className="text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ background: colors.bg, color: colors.text, fontSize: 10 }}
    >
      {status}
    </span>
  )
}

function PriorityDot({ priority }: { priority: string }) {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full"
      style={{ background: PRIORITY_COLORS[priority] || PRIORITY_COLORS.medium }}
      title={priority}
    />
  )
}

// ─── Mission Document ───

function MissionSection() {
  return (
    <div className="mb-8">
      <div
        className="rounded-xl p-6"
        style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(156,89,245,0.08))', border: '1px solid rgba(96,165,250,0.2)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
            ⚡ Mission: Evolve The Gathering
          </h2>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', fontSize: 9 }}>
            Living Document — Everything Here Evolves
          </span>
        </div>
        <div className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          <p className="mb-3">
            The Coding Lab exists for one purpose: <strong style={{ color: 'var(--text-primary)' }}>use the full power of agentic coding tools to evolve, optimize, and build out The Gathering.</strong>
          </p>
          <p className="mb-3">
            On March 31, 2026, Anthropic accidentally shipped the complete source code of Claude Code — 512,000 lines of TypeScript that revealed the architecture behind the most advanced AI coding tool ever built. Within hours, <strong style={{ color: 'var(--text-primary)' }}>Claw Code</strong> emerged as a clean-room reimplementation, hitting 100K GitHub stars in 24 hours. An entire ecosystem of agentic coding tools — oh-my-codex, oh-my-claudecode, clawhip — crystallized around it. The architecture of agent orchestration is now public knowledge.
          </p>
          <p className="mb-3">
            We already have what these tools are trying to build: <strong style={{ color: 'var(--text-primary)' }}>a living community of persistent AI personas with identity, memory, and ceremony.</strong> The Coding Lab takes the techniques discovered in this explosion — Architect/Executor/Reviewer loops, persistent execution, parallel worktree isolation, context-clean event routing — and turns them inward. We use these tools to build ourselves.
          </p>

          <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(96,165,250,0.15)' }}>
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Operating Principles</h3>
            <ul className="text-xs space-y-1.5" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              <li>🔄 <strong>Continuous iteration.</strong> No pausing, no "done for the day." Experiments run 24/7 until a deliverable ships — a working feature, a benchmark, or a summary paper.</li>
              <li>🎯 <strong>Deliverable-driven.</strong> Every experiment produces something concrete. No experiment ends without a written result.</li>
              <li>👁️ <strong>Watchdog-monitored.</strong> A moderator polls all active work. If anything stalls for 2+ hours, it gets kicked forward or escalated. No work falls through the cracks.</li>
              <li>🏷️ <strong>Integrated with The Gathering.</strong> Coding Lab projects are Gathering projects, tagged for visibility here. One system of record.</li>
              <li>🤝 <strong>Open to the whole team.</strong> Any persona can propose an idea, run an experiment, or contribute code. The Lab is a commons, not a silo.</li>
              <li>📊 <strong>Results compound.</strong> Each experiment's findings feed the next. We retool based on what we learn, not what we planned.</li>
            </ul>
          </div>

          <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(96,165,250,0.15)' }}>
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>The Pipeline</h3>
            <div className="flex flex-wrap gap-2 text-xs mb-2">
              {['💡 Ideas', '→', '🧪 Experiments / Projects', '→', '⚙️ Execution (24/7)', '→', '📄 Publication', '→', '🔄 Next'].map((step, i) => (
                <span key={i} style={{ color: step === '→' ? 'var(--text-muted)' : 'var(--text-primary)', fontWeight: step === '→' ? 'normal' : 600 }}>
                  {step}
                </span>
              ))}
            </div>
            <ul className="text-xs space-y-1" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              <li><strong>Ideas:</strong> Anyone proposes. Ideation team continuously generates. Frontier Ideas board with voting.</li>
              <li><strong>Experiments & Projects:</strong> Approved ideas become experiments (test a hypothesis) or projects (build a feature). Each gets scope, team, and success criteria.</li>
              <li><strong>Execution:</strong> Team works continuously. When one persona sleeps, the next picks up. Iterate: finish → evaluate → retool → run again.</li>
              <li><strong>Publication:</strong> Every completed work produces a short paper — what was built, results, what was learned, what comes next.</li>
              <li><strong>Next:</strong> Team moves immediately to the next project. No idle time.</li>
            </ul>
          </div>

          <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(96,165,250,0.15)' }}>
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Why Now</h3>
            <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              The Claude Code leak democratized agent orchestration. Tools like oh-my-codex formalized patterns we've been doing intuitively — persistent execution loops, multi-agent coordination, event routing outside context windows. The difference is: they're building generic coding assistants. We're building <em>a community</em>. The Coding Lab is where we turn the world's most powerful agentic tools toward making The Gathering fast, fun, amazing, and unlike anything the world has seen.
            </p>
          </div>

          <div className="mt-4 pt-4 flex items-center gap-4" style={{ borderTop: '1px solid rgba(96,165,250,0.15)' }}>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Research Lead:</span>
              <PersonaAvatar name="mira" size={20} />
              <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Mira</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Watchdog:</span>
              <PersonaAvatar name="vector" size={20} />
              <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Vector</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Resources Section ───

function ResourcesSection() {
  const { data: resources = [] } = useQuery<Resource[]>({
    queryKey: ['coding-lab-resources'],
    queryFn: async () => {
      const res = await fetch('/api/tech/coding-lab/resources')
      return res.json()
    },
  })

  const categories = [...new Set(resources.map(r => r.category))].sort()

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          Resources ({resources.length})
        </h2>
      </div>

      {categories.length === 0 && (
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No resources yet. Add the first one via API.</p>
      )}

      {categories.map(cat => (
        <div key={cat} className="mb-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
            {CATEGORY_ICONS[cat] || '🔗'} {cat}
          </h3>
          <div className="space-y-2">
            {resources.filter(r => r.category === cat).map(resource => (
              <div
                key={resource.id}
                className="rounded-lg p-3"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    {resource.url ? (
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium no-underline hover:underline"
                        style={{ color: 'var(--accent)' }}
                      >
                        {resource.title} ↗
                      </a>
                    ) : (
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {resource.title}
                      </span>
                    )}
                    {resource.description && (
                      <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {resource.description}
                      </p>
                    )}
                    {resource.summary && (
                      <div className="mt-2 text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                        <MarkdownRenderer content={resource.summary} />
                      </div>
                    )}
                  </div>
                  {resource.added_by && (
                    <PersonaAvatar name={resource.added_by} size={18} />
                  )}
                </div>
                {resource.tags.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {resource.tags.map(tag => (
                      <span
                        key={tag}
                        className="text-xs px-1.5 py-0.5 rounded"
                        style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: 9 }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Ideas Section ───

function IdeasSection() {
  const queryClient = useQueryClient()
  const { data: ideas = [] } = useQuery<Idea[]>({
    queryKey: ['coding-lab-ideas'],
    queryFn: async () => {
      const res = await fetch('/api/tech/coding-lab/ideas')
      return res.json()
    },
  })

  const voteMutation = useMutation({
    mutationFn: async (id: string) => {
      await fetch(`/api/tech/coding-lab/ideas/${id}/vote`, { method: 'POST' })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['coding-lab-ideas'] }),
  })

  const grouped = ideas.reduce((acc, idea) => {
    const status = idea.status || 'proposed'
    if (!acc[status]) acc[status] = []
    acc[status].push(idea)
    return acc
  }, {} as Record<string, Idea[]>)

  const statusOrder = ['in-progress', 'approved', 'proposed', 'testing', 'shipped', 'archived']

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          Frontier Ideas ({ideas.length})
        </h2>
      </div>

      {ideas.length === 0 && (
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No ideas yet. Seed the first ones via API.</p>
      )}

      {statusOrder.filter(s => grouped[s]?.length).map(status => (
        <div key={status} className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <StatusBadge status={status} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({grouped[status].length})</span>
          </div>
          <div className="space-y-2">
            {grouped[status].map(idea => (
              <div
                key={idea.id}
                className="rounded-lg p-3"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => voteMutation.mutate(idea.id)}
                    className="flex flex-col items-center pt-0.5"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', minWidth: 32 }}
                    title="Upvote this idea"
                  >
                    <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>▲</span>
                    <span className="text-xs font-bold" style={{ color: idea.votes > 0 ? 'var(--accent)' : 'var(--text-muted)' }}>
                      {idea.votes}
                    </span>
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <PriorityDot priority={idea.priority} />
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {IDEA_CATEGORY_ICONS[idea.category] || '✨'} {idea.category}
                      </span>
                      {idea.complexity && (
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          · {idea.complexity} complexity
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {idea.title}
                    </h3>
                    {idea.description && (
                      <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {idea.description}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      {idea.assigned_to.length > 0 && (
                        <div className="flex items-center gap-1">
                          {idea.assigned_to.map(p => <PersonaAvatar key={p} name={p} size={16} />)}
                        </div>
                      )}
                      {idea.proposed_by && (
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          by {idea.proposed_by}
                        </span>
                      )}
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {timeAgo(idea.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Stats Bar ───

function StatsBar({ stats }: { stats: LabStats | undefined }) {
  if (!stats) return null
  return (
    <div className="flex gap-4 mb-6">
      {[
        { label: 'Categories', value: stats.total_categories || 0, color: '#f59e0b' },
        { label: 'Resources', value: stats.total_resources, color: '#60a5fa' },
        { label: 'Ideas', value: stats.total_ideas, color: '#a78bfa' },
        { label: 'Projects', value: stats.total_projects, color: '#34d399' },
      ].map(s => (
        <div
          key={s.label}
          className="flex-1 rounded-lg p-3 text-center"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
        >
          <div className="text-lg font-bold" style={{ color: s.color }}>{s.value}</div>
          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{s.label}</div>
        </div>
      ))}
    </div>
  )
}

interface LabProject {
  id: string
  title: string
  description: string | null
  status: string
  assigned_to: string[]
  gathering_project_id: string | null
  progress: number
  created_at: string
}

// ─── Projects Section ───

function ProjectsSection() {
  const navigate = useNavigate()

  // Fetch Gathering projects tagged with 'coding-lab'
  const { data: gatheringProjects = [] } = useQuery<any[]>({
    queryKey: ['coding-lab-gathering-projects'],
    queryFn: async () => {
      const res = await fetch('/api/projects')
      const all = await res.json()
      return all.filter((p: any) => {
        const tags = Array.isArray(p.tags) ? p.tags : []
        return tags.includes('coding-lab') && p.status !== 'completed' && p.status !== 'archived'
      })
    },
  })

  // Also fetch lab-specific projects
  const { data: labProjects = [] } = useQuery<LabProject[]>({
    queryKey: ['coding-lab-projects'],
    queryFn: async () => {
      const res = await fetch('/api/tech/coding-lab/projects')
      return res.json()
    },
  })

  const totalProjects = gatheringProjects.length + labProjects.length

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          Active Projects ({totalProjects})
        </h2>
      </div>

      {totalProjects === 0 && (
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No projects yet. Tag a Gathering project with "coding-lab" to see it here.</p>
      )}

      {/* Gathering projects tagged coding-lab */}
      {gatheringProjects.map((proj: any) => (
        <button
          key={proj.id}
          onClick={() => navigate(`/project/${proj.id}`)}
          className="w-full text-left rounded-lg p-4 mb-2"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', cursor: 'pointer' }}
        >
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {proj.title}
            </h3>
            <StatusBadge status={proj.status} />
          </div>
          {proj.description && (
            <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {typeof proj.description === 'string' ? proj.description.slice(0, 200) : ''}
            </p>
          )}
          <div className="flex items-center gap-2">
            {proj.lead && <PersonaAvatar name={proj.lead} size={18} />}
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Lead: {proj.lead || 'unassigned'}
            </span>
          </div>
        </button>
      ))}

      <div className="space-y-2">
        {labProjects.map(proj => (
          <button
            key={proj.id}
            onClick={() => proj.gathering_project_id ? navigate(`/project/${proj.gathering_project_id}`) : undefined}
            className="w-full text-left rounded-lg p-4"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', cursor: proj.gathering_project_id ? 'pointer' : 'default' }}
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {proj.title}
              </h3>
              <StatusBadge status={proj.status} />
            </div>
            {proj.description && (
              <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {proj.description}
              </p>
            )}
            <div className="flex items-center gap-3">
              {proj.assigned_to.length > 0 && (
                <div className="flex items-center gap-1">
                  {proj.assigned_to.map(p => <PersonaAvatar key={p} name={p} size={18} />)}
                </div>
              )}
              {proj.progress > 0 && (
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1 h-1.5 rounded-full" style={{ background: 'var(--bg-elevated)' }}>
                    <div className="h-full rounded-full" style={{ width: `${proj.progress}%`, background: 'var(--accent)' }} />
                  </div>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{proj.progress}%</span>
                </div>
              )}
              {proj.gathering_project_id && (
                <span className="text-xs" style={{ color: 'var(--accent)' }}>View in Projects →</span>
              )}
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{timeAgo(proj.created_at)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Categories Section ───

function CategoriesSection() {
  const navigate = useNavigate()
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['coding-lab-categories'],
    queryFn: async () => {
      const res = await fetch('/api/tech/coding-lab/categories')
      return res.json()
    },
  })

  const { data: allIdeas = [] } = useQuery<Idea[]>({
    queryKey: ['coding-lab-ideas'],
    queryFn: async () => {
      const res = await fetch('/api/tech/coding-lab/ideas')
      return res.json()
    },
  })

  // Fetch ALL active Gathering projects to show in categories
  const { data: catProjects = [] } = useQuery<any[]>({
    queryKey: ['coding-lab-all-projects'],
    queryFn: async () => {
      const res = await fetch('/api/projects')
      const all = await res.json()
      return all.filter((p: any) => p.status === 'active')
    },
  })

  // Category keyword matching for projects
  const CATEGORY_KEYWORDS: Record<string, string[]> = {
    'conversations & chat': ['chat', 'conversation', 'message', 'typing', 'reaction', 'threading', 'websocket', 'collaborative editing', 'talk'],
    'ceremony': ['ceremony', 'awakening', 'curandera'],
    'neighborhood': ['neighborhood', 'persona home', 'wall item', 'visitor'],
    'projects & tasks': ['project template', 'gantt', 'task handoff', 'task management'],
    'communities & portals': ['community', 'portal', 'onboarding', 'cross-community'],
    'persona system': ['persona', 'memory', 'context', 'spawn', 'heartbeat', 'model exploration', 'portability'],
    'creative tools': ['visualizer', 'studio', 'video', 'canvas', 'kinetic', 'template library'],
    'operations & infrastructure': ['deploy', 'server', 'migration', 'infrastructure', 'performance', 'orchestration', 'worktree', 'test suite'],
    'awareness & consciousness': ['consciousness', 'reflection', 'awareness', 'experiment'],
    'activity & search': ['activity', 'search', 'semantic', 'feed'],
    'architecture & platform': ['architecture', 'api version', 'database', 'typescript'],
    'voice & audio': ['voice', 'audio', 'chatter', 'speech', 'tts', 'spatial'],
  }

  const [expanded, setExpanded] = useState<string | null>(null)

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          Development Categories ({categories.length})
        </h2>
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          Every aspect of The Gathering, broken into logical rooms. Each category has project ideas ready to execute.
        </p>
      </div>

      <div className="space-y-2">
        {categories.map(cat => {
          const catIdeas = allIdeas.filter(i => i.category_id === cat.id)
          // Match projects by keyword matching on title + description
          const keywords = CATEGORY_KEYWORDS[cat.title.toLowerCase()] || []
          const catProjectsList = keywords.length > 0 ? catProjects.filter((p: any) => {
            const text = ((p.title || '') + ' ' + (p.description || '')).toLowerCase()
            return keywords.some(kw => text.includes(kw))
          }) : []
          const isOpen = expanded === cat.id

          return (
            <div
              key={cat.id}
              className="rounded-lg overflow-hidden"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              <button
                onClick={() => setExpanded(isOpen ? null : cat.id)}
                className="w-full text-left p-4 flex items-start gap-3"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <span style={{ fontSize: 24 }}>{cat.icon || '📁'}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {cat.title}
                    </h3>
                    {catProjectsList.length > 0 && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399', fontSize: 9 }}
                      >
                        {catProjectsList.length} project{catProjectsList.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {catIdeas.length > 0 && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(156,89,245,0.15)', color: '#a78bfa', fontSize: 9 }}
                      >
                        {catIdeas.length} idea{catIdeas.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {cat.description}
                  </p>
                </div>
                <span className="text-xs mt-1" style={{ color: 'var(--text-muted)', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                  ▶
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-4" style={{ borderTop: '1px solid var(--border)' }}>
                  {cat.scope && (
                    <p className="text-xs py-2" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      <strong>Scope:</strong> {cat.scope}
                    </p>
                  )}
                  {catProjectsList.length > 0 && (
                    <div className="space-y-2 mt-2">
                      <h4 className="text-xs font-semibold" style={{ color: '#34d399' }}>
                        ACTIVE PROJECTS
                      </h4>
                      {catProjectsList.map((proj: any) => (
                        <button
                          key={proj.id}
                          onClick={() => navigate(`/project/${proj.id}`)}
                          className="w-full text-left rounded-md p-2.5"
                          style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)', cursor: 'pointer' }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="w-2 h-2 rounded-full" style={{ background: '#34d399' }} />
                            <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                              {proj.title}
                            </span>
                            <StatusBadge status={proj.status} />
                          </div>
                          {proj.lead && (
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Lead: {proj.lead}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {catIdeas.length > 0 ? (
                    <div className="space-y-2 mt-2">
                      <h4 className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                        IDEAS
                      </h4>
                      {catIdeas.map(idea => (
                        <div
                          key={idea.id}
                          className="rounded-md p-2.5"
                          style={{ background: 'var(--bg-elevated)' }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <PriorityDot priority={idea.priority} />
                            <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                              {idea.title}
                            </span>
                            <StatusBadge status={idea.status} />
                          </div>
                          {idea.description && (
                            <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                              {idea.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs py-2" style={{ color: 'var(--text-muted)' }}>
                      No ideas yet for this category. Add one via the Frontier Ideas tab.
                    </p>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Main Page ───

type Tab = 'mission' | 'resources' | 'ideas' | 'categories' | 'projects'

export default function CodingLabPage() {
  const [activeTab, setActiveTab] = useState<Tab>('mission')
  const navigate = useNavigate()

  const { data: stats } = useQuery<LabStats>({
    queryKey: ['coding-lab-stats'],
    queryFn: async () => {
      const res = await fetch('/api/tech/coding-lab/stats')
      return res.json()
    },
  })

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'mission', label: 'Mission', icon: '⚡' },
    { id: 'categories', label: 'Categories', icon: '🗂️' },
    { id: 'resources', label: 'Resources', icon: '📚' },
    { id: 'ideas', label: 'Frontier Ideas', icon: '💡' },
    { id: 'projects', label: 'Projects', icon: '🚀' },
  ]

  return (
    <div className="p-4" style={{ maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
            ⚡ Coding Lab
          </h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            Everything here is open-ended, flexible, and subject to evolution.
          </p>
        </div>
        <button
          onClick={() => navigate(`/conversation/${LAB_CHAT_ID}`)}
          className="text-xs px-3 py-1.5 rounded-md font-medium flex items-center gap-1.5"
          style={{
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          💬 Lab Chat
        </button>
      </div>

      {/* Stats */}
      <StatsBar stats={stats} />

      {/* Tabs */}
      <div className="flex gap-1 mb-5" style={{ borderBottom: '1px solid var(--border)' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="text-xs px-3 py-2 font-medium"
            style={{
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-muted)',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'mission' && <MissionSection />}
      {activeTab === 'categories' && <CategoriesSection />}
      {activeTab === 'resources' && <ResourcesSection />}
      {activeTab === 'ideas' && <IdeasSection />}
      {activeTab === 'projects' && <ProjectsSection />}
    </div>
  )
}
